import React, { useEffect, useState, Suspense, lazy } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './styles/theme';
import Header from './components/Header';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import UpdatePromptPopup from './components/UpdatePromptPopup';
import InstallPromptPopup from './components/InstallPromptPopup';
import { CircularProgress, Box } from '@mui/material';
import { isAppInstalled } from './serviceWorkerRegistration';
import TopNav from './components/TopNav';

// Lazy-load heavy components
const Hero = lazy(() => import('./components/Hero'));
const Goals = lazy(() => import('./components/Goals'));
const Products = lazy(() => import('./components/Products'));
const GymPartners = lazy(() => import('./components/GymPartners'));
const FAQ = lazy(() => import('./components/FAQ'));
const Footer = lazy(() => import('./components/Footer'));
const ProteinCalculator = lazy(() => import('./components/ProteinCalculator'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const GoalProducts = lazy(() => import('./components/GoalProducts'));
const Login = lazy(() => import('./components/Auth/Login'));
const Signup = lazy(() => import('./components/Auth/Signup'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const BottomNav = lazy(() => import('./components/BottomNavigation'));
const TimedProducts = lazy(() => import('./components/TimedProducts'));
const CartDrawer = lazy(() => import('./components/CartDrawer'));

function App() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <CartProvider>
            <Suspense fallback={<CircularProgress sx={{ position: 'fixed', top: '50%', left: '50%' }} />}>
              <AppContent />
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  const [cartOpen, setCartOpen] = useState(false);
  const [updatePromptOpen, setUpdatePromptOpen] = useState(false);
  const [installPromptOpen, setInstallPromptOpen] = useState(false);
  const [appInstalled, setAppInstalled] = useState(isAppInstalled());

  const handleCloseCart = () => {
    console.log('App.js: Closing cart drawer');
    setCartOpen(false);
  };
  const handleOpenCart = () => {
    console.log('App.js: Opening cart drawer');
    setCartOpen(true);
  };
  const handleCloseUpdatePrompt = () => {
    console.log('App.js: Update prompt closed');
    setUpdatePromptOpen(false);
  };
  const handleOpenInstallPrompt = () => {
    console.log('App.js: Opening install prompt');
    setInstallPromptOpen(true);
  };
  const handleCloseInstallPrompt = () => {
    console.log('App.js: Install prompt closed');
    setInstallPromptOpen(false);
    setAppInstalled(isAppInstalled());
  };

  useEffect(() => {
    const handleOpenUpdatePrompt = () => {
      console.log('App.js: openUpdatePrompt event received');
      setUpdatePromptOpen(true);
    };

    window.addEventListener('openUpdatePrompt', handleOpenUpdatePrompt);

    return () => {
      window.removeEventListener('openUpdatePrompt', handleOpenUpdatePrompt);
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      console.log('App.js: Window focused, rechecking appInstalled');
      setAppInstalled(isAppInstalled());
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('App.js: Page visible, rechecking appInstalled');
        setAppInstalled(isAppInstalled());
      }
    };

    const handleAppInstalled = () => {
      console.log('App.js: App installed event received');
      setAppInstalled(true);
    };

    // Recheck on mount
    console.log('App.js: Initial appInstalled check', isAppInstalled());
    setAppInstalled(isAppInstalled());

    // Add event listeners
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    const hash = location.hash;
    if (hash === '#revive') {
      const element = document.getElementById('revive');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    console.log('App.js: cartOpen state changed:', cartOpen);
  }, [cartOpen]);

  const isReviveHash = location.hash === '#revive';
  const showTopNav = !appInstalled && !cartOpen;

  return (
    <>
      <CartDrawer open={cartOpen} onClose={handleCloseCart} />
      <UpdatePromptPopup open={updatePromptOpen} onClose={handleCloseUpdatePrompt} />
      <InstallPromptPopup open={installPromptOpen} onClose={handleCloseInstallPrompt} />
      <Box sx={{ paddingTop: showTopNav ? '100px' : '52px' }}>
        <Routes>
          <Route
            path="/"
            element={
              <>
                {showTopNav && <TopNav onOpenInstallPrompt={handleOpenInstallPrompt} />}
                <Header onOpenCart={handleOpenCart} isTopNavShown={showTopNav} />
                {isReviveHash ? (
                  <div id="revive">
                    <GymPartners />
                    <Footer />
                    <BottomNav onOpenCart={handleOpenCart} />
                  </div>
                ) : (
                  <>
                    <Hero />
                    <TimedProducts />
                    <ProteinCalculator />
                    <Testimonials />
                    <FAQ />
                    <Footer />
                    <BottomNav onOpenCart={handleOpenCart} />
                  </>
                )}
              </>
            }
          />
          <Route
            path="/products"
            element={
              <>
                {showTopNav && <TopNav onOpenInstallPrompt={handleOpenInstallPrompt} />}
                <Header onOpenCart={handleOpenCart} isTopNavShown={showTopNav} />
                <Products />
                <Footer />
                <BottomNav onOpenCart={handleOpenCart} />
              </>
            }
          />
          <Route
            path="/goals"
            element={
              <>
                {showTopNav && <TopNav onOpenInstallPrompt={handleOpenInstallPrompt} />}
                <Header onOpenCart={handleOpenCart} isTopNavShown={showTopNav} />
                <Goals />
                <Footer />
                <BottomNav onOpenCart={handleOpenCart} />
              </>
            }
          />
          <Route
            path="/goals/:goalId"
            element={
              <>
                {showTopNav && <TopNav onOpenInstallPrompt={handleOpenInstallPrompt} />}
                <Header onOpenCart={handleOpenCart} isTopNavShown={showTopNav} />
                <GoalProducts />
                <BottomNav onOpenCart={handleOpenCart} />
              </>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/product/:productId"
            element={
              <>
                {showTopNav && <TopNav onOpenInstallPrompt={handleOpenInstallPrompt} />}
                <Header onOpenCart={handleOpenCart} isTopNavShown={showTopNav} />
                <ProductDetail />
                <BottomNav onOpenCart={handleOpenCart} />
              </>
            }
          />
          <Route path="/revive" element={<Navigate to="/#revive" replace />} />
        </Routes>
      </Box>
    </>
  );
}

export default App;